import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  BarChart3,
  Bot,
  CheckCircle,
  Clock,
  Cpu,
  FileText,
  Gauge,
  Layers,
  MessageCircle,
  Phone,
  Rocket,
  Send,
  ShieldCheck,
  Users,
  Workflow,
  X
} from 'lucide-react';

import ContactForm from '../ContactForm';
import LanguageSwitcher from '../components/LanguageSwitcher';

const COPY = {
  es: {
    nav: {
      cta: 'Solicitar diagnostico',
      support: 'Soporte Pro'
    },
    hero: {
      badge: 'CRM Advisory by Nextcoders',
      title: 'CRM con criterio operativo, no solo software',
      subtitle:
        'Diseñamos, ordenamos y gobernamos tu operacion comercial para que ventas, seguimiento y servicio trabajen con la misma logica.',
      desc:
        'Nextcoders actua como advisor de CRM: definimos procesos, arquitectura, automatizaciones, tableros e integraciones para que tu sistema refleje como opera realmente tu negocio.',
      ctaPrimary: 'Solicitar diagnostico',
      ctaSecondary: 'Ver roadmap'
    },
    overview: {
      title: 'Como trabajamos la capa CRM',
      subtitle:
        'No partimos de pantallas ni modulos sueltos. Partimos del proceso, de las personas y de la trazabilidad que la direccion necesita.',
      items: [
        {
          icon: 'layers',
          title: 'Arquitectura comercial',
          description:
            'Definimos etapas, reglas, handoffs y estructura de datos para que el CRM sostenga la operacion, no la complique.'
        },
        {
          icon: 'rocket',
          title: 'Implementacion guiada por negocio',
          description:
            'Traducimos objetivos comerciales en automatizaciones, alertas, prioridades y paneles que el equipo pueda usar desde el primer dia.'
        },
        {
          icon: 'workflow',
          title: 'Gobernanza y evolucion',
          description:
            'Instalamos criterio de ownership, control de calidad y mejora continua para que el CRM crezca con la empresa.'
        }
      ]
    },
    capabilities: {
      title: 'Lo que una operacion madura necesita',
      subtitle:
        'Un CRM bien asesorado debe ordenar la ejecucion diaria y al mismo tiempo darle visibilidad real a direccion.',
      list: [
        'Pipeline con criterios claros de avance y salida',
        'Automatizaciones para seguimiento, asignacion y alertas',
        'Gobernanza de campos, responsables y SLA',
        'Visibilidad del ciclo comercial y postventa',
        'Tableros accionables para gerencia y jefaturas',
        'Integraciones con canales, formularios y sistemas clave'
      ]
    },
    workspace: {
      title: 'Las capas que alineamos',
      subtitle:
        'El asesoramiento CRM cruza operacion, equipo, datos y control para que todos trabajen con el mismo mapa.',
      areas: ['Pipeline', 'Seguimiento', 'Servicio', 'Reportes', 'Gobernanza'],
      differentiators: [
        'Menos criterio individual y mas reglas compartidas',
        'Tiempos de respuesta medibles y auditables',
        'Datos mas limpios para decisiones comerciales',
        'Menos friccion entre ventas, operaciones y soporte'
      ]
    },
    journey: {
      title: 'Roadmap de asesoramiento CRM',
      subtitle:
        'Trabajamos desde el diagnostico hasta la adopcion con una secuencia pensada para que el sistema refleje la realidad del equipo.',
      flowTitle: 'Del caos comercial a una operacion trazable',
      rolesTitle: 'Criterio de ownership',
      rolesDesc:
        'Definimos quien captura, quien califica, quien ejecuta, quien supervisa y quien responde por la calidad del dato en cada etapa.',
      steps: [
        { title: 'Diagnostico', description: 'Levantamos el flujo actual, cuellos de botella y perdida de contexto.' },
        { title: 'Arquitectura', description: 'Diseñamos pipeline, estados, responsables y reglas de negocio.' },
        { title: 'Implementacion', description: 'Configuramos automatizaciones, formularios, vistas y tableros.' },
        { title: 'Adopcion', description: 'Aterrizamos el uso operativo por rol con criterios simples y medibles.' },
        { title: 'Control', description: 'Instalamos indicadores para direccion, jefaturas y seguimiento diario.' },
        { title: 'Mejora continua', description: 'Ajustamos segun fricciones reales y nuevas prioridades del negocio.' }
      ]
    },
    security: {
      title: 'Gobierno del dato comercial',
      subtitle:
        'La seguridad de un CRM no es solo acceso. Tambien es contexto, calidad, consistencia y trazabilidad.',
      layers: [
        {
          icon: 'shield',
          title: 'Acceso por rol',
          description: 'Definimos permisos, responsables y alcance operativo para reducir errores y exposiciones innecesarias.'
        },
        {
          icon: 'layers',
          title: 'Estructura consistente',
          description: 'Estandarizamos campos, catálogos y estados para que la informacion sea comparable y util.'
        },
        {
          icon: 'send',
          title: 'Trazabilidad',
          description: 'Cada cambio relevante debe dejar rastro para supervisar cumplimiento, seguimiento y calidad.'
        },
        {
          icon: 'file',
          title: 'Documentacion operativa',
          description: 'El sistema debe tener criterio escrito para que el equipo no dependa de memoria informal.'
        }
      ]
    },
    implementation: {
      title: 'Que resolvemos en una asesoría CRM',
      subtitle:
        'La mayoria de problemas no aparecen por falta de tecnologia, sino por falta de diseño operacional y gobierno.',
      problems: [
        { title: 'Pipeline inflado', description: 'Etapas ambiguas, oportunidades mal calificadas y seguimiento sin criterio comun.' },
        { title: 'Automatizacion desordenada', description: 'Reglas superpuestas, notificaciones irrelevantes y tareas que nadie gobierna.' },
        { title: 'Dato poco confiable', description: 'Campos incompletos, capturas inconsistentes y reportes que no sirven para decidir.' },
        { title: 'Baja adopcion', description: 'El equipo siente que el CRM complica en vez de acelerar la operacion.' }
      ],
      deliverablesTitle: 'Entregables de una operacion CRM bien asesorada',
      deliverablesSubtitle:
        'No solo dejamos configuracion. Dejamos estructura, criterio y visibilidad para sostener el uso diario.',
      deliverables: [
        { icon: 'workflow', title: 'Pipeline y reglas', description: 'Etapas, criterios de avance, ownership y handoffs documentados.' },
        { icon: 'file', title: 'Modelo de datos', description: 'Campos clave, taxonomias y reglas de captura alineadas al negocio.' },
        { icon: 'bot', title: 'Automatizaciones utiles', description: 'Seguimientos, alertas y tareas que reducen friccion y tiempos muertos.' },
        { icon: 'shield', title: 'Gobernanza', description: 'Permisos, responsabilidades y criterios de calidad para sostener el orden.' },
        { icon: 'cpu', title: 'Integraciones clave', description: 'Conectamos formularios, canales y sistemas donde generan valor real.' },
        { icon: 'gauge', title: 'KPIs accionables', description: 'Tableros para direccion y jefaturas con foco en conversion, tiempos y control.' }
      ],
      dashboardTitle: 'Indicadores que importa mirar',
      gauges: [
        { label: 'Visibilidad', value: 96, caption: 'Direccion puede entender que esta pasando y donde intervenir.' },
        { label: 'Automatizacion util', value: 82, caption: 'Las reglas alivian la operacion en vez de generar ruido.' },
        { label: 'Calidad del dato', value: 89, caption: 'El equipo captura informacion que si sirve para medir y decidir.' }
      ],
      layers: {
        title: 'CRM en capas',
        core: 'CRM',
        coreDesc: 'Operacion diaria',
        outer: 'Proceso',
        outerDesc: 'Reglas y ownership',
        security: 'Gobernanza',
        securityDesc: 'Calidad, trazabilidad y control',
        description:
          'El CRM es solo el nucleo. Alrededor deben existir criterio de proceso, gobierno del dato y acompanamiento para que la operacion escale con orden.'
      },
      pillars: [
        {
          icon: 'file',
          title: 'DAP',
          description: 'Mapeamos el proceso, documentamos decisiones y aterrizamos el criterio operativo.',
          href: '/dap',
          ctaLabel: 'Explorar'
        },
        {
          icon: 'workflow',
          title: 'SDI',
          description: 'Configuramos e implementamos la arquitectura necesaria para ejecutar el CRM con orden.',
          href: '/sdi',
          ctaLabel: 'Explorar'
        },
        {
          icon: 'bar',
          title: 'SMC',
          description: 'Auditamos, mejoramos y evolucionamos la operacion segun la realidad del negocio.',
          href: '/smc',
          ctaLabel: 'Explorar'
        },
        {
          icon: 'phone',
          title: 'Soporte Pro',
          description: 'Sostenemos la operacion con acompanamiento tecnico y resolucion orientada a continuidad.',
          href: '/soporte',
          ctaLabel: 'Ver Soporte Pro'
        }
      ]
    },
    packs: {
      title: 'Tres niveles de acompañamiento CRM',
      subtitle:
        'Puedes comenzar por diagnostico, avanzar a implementacion guiada o trabajar una capa integral de arquitectura y mejora continua.',
      viewMore: 'Ver detalle',
      viewLess: 'Ver menos',
      evaluate: 'Quiero evaluar mi operacion',
      items: [
        {
          title: 'Foundation',
          subtitle: 'Ordenar la base operativa',
          recommended: false,
          links: ['DAP', 'SDI', 'Soporte Pro'],
          sections: [
            { label: 'Diagnostico', value: 'Base clara', width: '42%', tone: 'gray' },
            { label: 'Pipeline', value: 'Estructura inicial', width: '48%', tone: 'gray' },
            { label: 'Seguimiento', value: 'Reglas esenciales', width: '54%', tone: 'emerald' },
            { label: 'Tableros', value: 'Visibilidad inicial', width: '45%', tone: 'gray' },
            { label: 'Gobernanza', value: 'Criterio minimo viable', width: '40%', tone: 'gray' },
            { label: 'Adopcion', value: 'Arranque controlado', width: '44%', tone: 'gray' }
          ]
        },
        {
          title: 'Growth',
          subtitle: 'Escalar con mas control',
          recommended: true,
          links: ['DAP', 'SDI', 'SMC', 'API Tool', 'Soporte Pro'],
          sections: [
            { label: 'Diagnostico', value: 'Cobertura por area', width: '66%', tone: 'blue' },
            { label: 'Pipeline', value: 'Embudos por flujo', width: '72%', tone: 'blue' },
            { label: 'Seguimiento', value: 'Automatizacion util', width: '76%', tone: 'emerald' },
            { label: 'Tableros', value: 'KPIs por rol', width: '70%', tone: 'blue' },
            { label: 'Gobernanza', value: 'Reglas auditables', width: '68%', tone: 'blue' },
            { label: 'Adopcion', value: 'Uso por ownership', width: '63%', tone: 'blue' }
          ]
        },
        {
          title: 'Scale',
          subtitle: 'Arquitectura y mejora continua',
          recommended: false,
          links: ['DAP', 'SDI', 'SMC', 'API Tool', 'Microservicio', 'Soporte Pro'],
          sections: [
            { label: 'Diagnostico', value: 'Cobertura ejecutiva', width: '90%', tone: 'indigo' },
            { label: 'Pipeline', value: 'Diseño multiproceso', width: '88%', tone: 'indigo' },
            { label: 'Seguimiento', value: 'Reglas avanzadas', width: '86%', tone: 'emerald' },
            { label: 'Tableros', value: 'Lectura directiva', width: '92%', tone: 'indigo' },
            { label: 'Gobernanza', value: 'Control integral', width: '94%', tone: 'indigo' },
            { label: 'Adopcion', value: 'Mejora sostenida', width: '82%', tone: 'indigo' }
          ]
        }
      ]
    },
    plans: {
      title: 'Donde aporta valor un advisor CRM',
      labels: {
        focus: 'Enfoque',
        withNextcoders: 'Con Nextcoders',
        fit: 'Ideal para'
      },
      items: [
        {
          title: 'Operacion comercial',
          desc: 'Cuando el equipo necesita orden, seguimiento y visibilidad para dejar de depender de criterio informal.',
          focus: 'Procesos, pipeline y ownership',
          withNextcoders: ['Reglas', 'Tableros', 'Seguimiento'],
          fit: ['Ventas', 'Account management', 'Atencion comercial'],
          tone: 'gray'
        },
        {
          title: 'Operacion de servicio',
          desc: 'Cuando la continuidad del cliente exige handoffs claros, trazabilidad y tiempos de respuesta medibles.',
          focus: 'Casos, SLA y continuidad',
          withNextcoders: ['Alertas', 'SLA', 'Supervision'],
          fit: ['Customer success', 'Soporte', 'Postventa'],
          tone: 'blue'
        },
        {
          title: 'Operacion integrada',
          desc: 'Cuando CRM, formularios, canales y sistemas deben trabajar con una sola logica operativa.',
          focus: 'Integracion y gobernanza',
          withNextcoders: ['Integraciones', 'API Tool', 'Microservicio'],
          fit: ['Direccion', 'Operaciones', 'Equipos multi-area'],
          tone: 'indigo'
        }
      ]
    },
    benefits: {
      title: 'Beneficios de asesorar bien el CRM',
      subtitle:
        'Un buen CRM no solo captura oportunidades. Ordena la ejecucion, acelera la respuesta y mejora la lectura del negocio.',
      items: [
        {
          icon: 'bar',
          title: 'Visibilidad ejecutiva',
          description: 'Direccion puede tomar decisiones con datos operativos consistentes y no con percepciones sueltas.'
        },
        {
          icon: 'message',
          title: 'Menos friccion entre equipos',
          description: 'Ventas, servicio y operaciones trabajan con el mismo estado de verdad y mejores handoffs.'
        },
        {
          icon: 'shield',
          title: 'Evolucion sostenible',
          description: 'La operacion puede crecer sin perder trazabilidad, orden y criterio de calidad.'
        }
      ]
    },
    final: {
      title: '¿Listo para ordenar tu operacion CRM con criterio senior?',
      desc:
        'No vendemos solo una herramienta. Te ayudamos a construir una operacion comercial que el equipo pueda ejecutar y la direccion pueda gobernar.',
      primary: 'Hablar con un consultor senior',
      secondary: 'Ver Soporte Pro',
      note: 'Nuestros tiempos de respuesta siguen los estandares de Soporte Pro.',
      explore: 'Tambien puedes explorar:',
      and: 'y'
    }
  },
  en: {
    nav: {
      cta: 'Request assessment',
      support: 'Pro Support'
    },
    hero: {
      badge: 'CRM Advisory by Nextcoders',
      title: 'CRM with operational criteria, not just software',
      subtitle:
        'We design, organize and govern your commercial operation so sales, follow-up and service run with the same logic.',
      desc:
        'Nextcoders acts as your CRM advisor: we define processes, architecture, automations, dashboards and integrations so the system reflects how your business actually works.',
      ctaPrimary: 'Request assessment',
      ctaSecondary: 'See roadmap'
    },
    overview: {
      title: 'How we approach the CRM layer',
      subtitle:
        'We do not start with screens or isolated modules. We start with process, ownership and the traceability leadership needs.',
      items: [
        {
          icon: 'layers',
          title: 'Commercial architecture',
          description:
            'We define stages, rules, handoffs and data structure so the CRM supports operations instead of getting in the way.'
        },
        {
          icon: 'rocket',
          title: 'Business-led implementation',
          description:
            'We translate commercial goals into automations, alerts, priorities and dashboards your team can use from day one.'
        },
        {
          icon: 'workflow',
          title: 'Governance and evolution',
          description:
            'We install ownership, quality control and continuous improvement so the CRM grows with the company.'
        }
      ]
    },
    capabilities: {
      title: 'What a mature operation needs',
      subtitle:
        'A well-advised CRM should organize daily execution while giving leadership real visibility.',
      list: [
        'Pipeline with clear advance and exit criteria',
        'Automations for follow-up, assignment and alerts',
        'Governance over fields, owners and SLA',
        'Visibility across the sales and service cycle',
        'Actionable dashboards for leadership and managers',
        'Integrations with channels, forms and key systems'
      ]
    },
    workspace: {
      title: 'The layers we align',
      subtitle:
        'CRM advisory crosses operations, team, data and control so everyone works from the same map.',
      areas: ['Pipeline', 'Follow-up', 'Service', 'Reporting', 'Governance'],
      differentiators: [
        'Less individual criteria and more shared rules',
        'Measurable and auditable response times',
        'Cleaner data for commercial decisions',
        'Less friction between sales, operations and support'
      ]
    },
    journey: {
      title: 'CRM advisory roadmap',
      subtitle:
        'We move from diagnosis to adoption with a sequence built to make the system match team reality.',
      flowTitle: 'From commercial chaos to a traceable operation',
      rolesTitle: 'Ownership criteria',
      rolesDesc:
        'We define who captures, who qualifies, who executes, who supervises and who is accountable for data quality at each stage.',
      steps: [
        { title: 'Diagnosis', description: 'We map the current flow, bottlenecks and context loss.' },
        { title: 'Architecture', description: 'We design pipeline, states, owners and business rules.' },
        { title: 'Implementation', description: 'We configure automations, forms, views and dashboards.' },
        { title: 'Adoption', description: 'We land role-based usage with simple and measurable criteria.' },
        { title: 'Control', description: 'We install indicators for leadership, managers and daily follow-up.' },
        { title: 'Continuous improvement', description: 'We adjust based on real friction and new business priorities.' }
      ]
    },
    security: {
      title: 'Commercial data governance',
      subtitle:
        'CRM security is not only access. It is also context, quality, consistency and traceability.',
      layers: [
        {
          icon: 'shield',
          title: 'Role-based access',
          description: 'We define permissions, owners and operational scope to reduce errors and unnecessary exposure.'
        },
        {
          icon: 'layers',
          title: 'Consistent structure',
          description: 'We standardize fields, catalogs and states so information becomes comparable and useful.'
        },
        {
          icon: 'send',
          title: 'Traceability',
          description: 'Each relevant change should leave a trail to supervise compliance, follow-up and quality.'
        },
        {
          icon: 'file',
          title: 'Operational documentation',
          description: 'The system needs written criteria so the team does not depend on informal memory.'
        }
      ]
    },
    implementation: {
      title: 'What we solve through CRM advisory',
      subtitle:
        'Most CRM problems do not come from missing technology. They come from missing operational design and governance.',
      problems: [
        { title: 'Inflated pipeline', description: 'Ambiguous stages, poorly qualified opportunities and follow-up with no shared criteria.' },
        { title: 'Messy automation', description: 'Overlapping rules, irrelevant notifications and tasks with no clear owner.' },
        { title: 'Weak data quality', description: 'Incomplete fields, inconsistent capture and reports nobody can trust.' },
        { title: 'Low adoption', description: 'The team feels the CRM adds friction instead of accelerating operations.' }
      ],
      deliverablesTitle: 'Deliverables of a well-advised CRM operation',
      deliverablesSubtitle:
        'We do not leave only configuration behind. We leave structure, criteria and visibility to sustain daily use.',
      deliverables: [
        { icon: 'workflow', title: 'Pipeline and rules', description: 'Stages, ownership, handoffs and advance criteria documented.' },
        { icon: 'file', title: 'Data model', description: 'Key fields, taxonomies and capture rules aligned with the business.' },
        { icon: 'bot', title: 'Useful automation', description: 'Follow-up, alerts and tasks that reduce friction and idle time.' },
        { icon: 'shield', title: 'Governance', description: 'Permissions, responsibilities and quality criteria to keep things ordered.' },
        { icon: 'cpu', title: 'Key integrations', description: 'We connect forms, channels and systems where they create real value.' },
        { icon: 'gauge', title: 'Actionable KPIs', description: 'Dashboards for leadership and managers focused on conversion, timing and control.' }
      ],
      dashboardTitle: 'Signals worth monitoring',
      gauges: [
        { label: 'Visibility', value: 96, caption: 'Leadership can understand what is happening and where to intervene.' },
        { label: 'Useful automation', value: 82, caption: 'Rules reduce operating load instead of adding noise.' },
        { label: 'Data quality', value: 89, caption: 'The team captures information that is actually usable for decisions.' }
      ],
      layers: {
        title: 'CRM in layers',
        core: 'CRM',
        coreDesc: 'Daily execution',
        outer: 'Process',
        outerDesc: 'Rules and ownership',
        security: 'Governance',
        securityDesc: 'Quality, traceability and control',
        description:
          'CRM is only the core. Around it there must be process criteria, data governance and ongoing guidance so the operation can scale with order.'
      },
      pillars: [
        {
          icon: 'file',
          title: 'DAP',
          description: 'We map the process, document decisions and land operational criteria.',
          href: '/dap',
          ctaLabel: 'Explore'
        },
        {
          icon: 'workflow',
          title: 'SDI',
          description: 'We configure and implement the architecture required to run CRM with order.',
          href: '/sdi',
          ctaLabel: 'Explore'
        },
        {
          icon: 'bar',
          title: 'SMC',
          description: 'We audit, improve and evolve the operation according to business reality.',
          href: '/smc',
          ctaLabel: 'Explore'
        },
        {
          icon: 'phone',
          title: 'Pro Support',
          description: 'We sustain operations with technical guidance and continuity-oriented resolution.',
          href: '/soporte',
          ctaLabel: 'See Pro Support'
        }
      ]
    },
    packs: {
      title: 'Three CRM advisory levels',
      subtitle:
        'You can start with diagnosis, move into guided implementation or work on a full architecture and continuous improvement layer.',
      viewMore: 'View detail',
      viewLess: 'View less',
      evaluate: 'Assess my operation',
      items: [
        {
          title: 'Foundation',
          subtitle: 'Organize the operational base',
          recommended: false,
          links: ['DAP', 'SDI', 'Pro Support'],
          sections: [
            { label: 'Diagnosis', value: 'Clear baseline', width: '42%', tone: 'gray' },
            { label: 'Pipeline', value: 'Initial structure', width: '48%', tone: 'gray' },
            { label: 'Follow-up', value: 'Core rules', width: '54%', tone: 'emerald' },
            { label: 'Dashboards', value: 'Initial visibility', width: '45%', tone: 'gray' },
            { label: 'Governance', value: 'Minimum viable criteria', width: '40%', tone: 'gray' },
            { label: 'Adoption', value: 'Controlled start', width: '44%', tone: 'gray' }
          ]
        },
        {
          title: 'Growth',
          subtitle: 'Scale with more control',
          recommended: true,
          links: ['DAP', 'SDI', 'SMC', 'API Tool', 'Pro Support'],
          sections: [
            { label: 'Diagnosis', value: 'Coverage by area', width: '66%', tone: 'blue' },
            { label: 'Pipeline', value: 'Flow-based pipelines', width: '72%', tone: 'blue' },
            { label: 'Follow-up', value: 'Useful automation', width: '76%', tone: 'emerald' },
            { label: 'Dashboards', value: 'Role-based KPIs', width: '70%', tone: 'blue' },
            { label: 'Governance', value: 'Auditable rules', width: '68%', tone: 'blue' },
            { label: 'Adoption', value: 'Ownership-based usage', width: '63%', tone: 'blue' }
          ]
        },
        {
          title: 'Scale',
          subtitle: 'Architecture and continuous improvement',
          recommended: false,
          links: ['DAP', 'SDI', 'SMC', 'API Tool', 'Microservice', 'Pro Support'],
          sections: [
            { label: 'Diagnosis', value: 'Executive coverage', width: '90%', tone: 'indigo' },
            { label: 'Pipeline', value: 'Multi-process design', width: '88%', tone: 'indigo' },
            { label: 'Follow-up', value: 'Advanced rules', width: '86%', tone: 'emerald' },
            { label: 'Dashboards', value: 'Leadership reading', width: '92%', tone: 'indigo' },
            { label: 'Governance', value: 'Full control', width: '94%', tone: 'indigo' },
            { label: 'Adoption', value: 'Sustained evolution', width: '82%', tone: 'indigo' }
          ]
        }
      ]
    },
    plans: {
      title: 'Where a CRM advisor creates value',
      labels: {
        focus: 'Focus',
        withNextcoders: 'With Nextcoders',
        fit: 'Best fit'
      },
      items: [
        {
          title: 'Commercial operations',
          desc: 'When the team needs order, follow-up and visibility instead of relying on informal criteria.',
          focus: 'Processes, pipeline and ownership',
          withNextcoders: ['Rules', 'Dashboards', 'Follow-up'],
          fit: ['Sales', 'Account management', 'Commercial service'],
          tone: 'gray'
        },
        {
          title: 'Service operations',
          desc: 'When customer continuity needs clear handoffs, traceability and measurable response times.',
          focus: 'Cases, SLA and continuity',
          withNextcoders: ['Alerts', 'SLA', 'Supervision'],
          fit: ['Customer success', 'Support', 'After-sales'],
          tone: 'blue'
        },
        {
          title: 'Integrated operations',
          desc: 'When CRM, forms, channels and systems need to work under one operational logic.',
          focus: 'Integration and governance',
          withNextcoders: ['Integrations', 'API Tool', 'Microservice'],
          fit: ['Leadership', 'Operations', 'Cross-functional teams'],
          tone: 'indigo'
        }
      ]
    },
    benefits: {
      title: 'Benefits of proper CRM advisory',
      subtitle:
        'A strong CRM does more than capture opportunities. It organizes execution, speeds response and improves business visibility.',
      items: [
        {
          icon: 'bar',
          title: 'Executive visibility',
          description: 'Leadership can decide using consistent operating data instead of fragmented perceptions.'
        },
        {
          icon: 'message',
          title: 'Less friction across teams',
          description: 'Sales, service and operations share the same truth and better handoffs.'
        },
        {
          icon: 'shield',
          title: 'Sustainable evolution',
          description: 'The operation can grow without losing traceability, order and quality criteria.'
        }
      ]
    },
    final: {
      title: 'Ready to organize your CRM operation with senior criteria?',
      desc:
        'We do not sell only a tool. We help you build a commercial operation your team can execute and leadership can govern.',
      primary: 'Talk to a senior consultant',
      secondary: 'See Pro Support',
      note: 'Our response times follow Pro Support standards.',
      explore: 'You can also explore:',
      and: 'and'
    }
  },
  pt: {
    nav: {
      cta: 'Solicitar diagnostico',
      support: 'Suporte Pro'
    },
    hero: {
      badge: 'CRM Advisory by Nextcoders',
      title: 'CRM com criterio operacional, nao apenas software',
      subtitle:
        'Desenhamos, organizamos e governamos sua operacao comercial para que vendas, acompanhamento e atendimento sigam a mesma logica.',
      desc:
        'A Nextcoders atua como advisor de CRM: definimos processos, arquitetura, automacoes, paineis e integracoes para que o sistema reflita como o seu negocio realmente opera.',
      ctaPrimary: 'Solicitar diagnostico',
      ctaSecondary: 'Ver roadmap'
    },
    overview: {
      title: 'Como tratamos a camada CRM',
      subtitle:
        'Nao comecamos por telas ou modulos soltos. Comecamos pelo processo, pelo ownership e pela rastreabilidade que a lideranca precisa.',
      items: [
        {
          icon: 'layers',
          title: 'Arquitetura comercial',
          description:
            'Definimos etapas, regras, handoffs e estrutura de dados para que o CRM sustente a operacao em vez de complicar.'
        },
        {
          icon: 'rocket',
          title: 'Implementacao guiada pelo negocio',
          description:
            'Traduzimos objetivos comerciais em automacoes, alertas, prioridades e paineis que a equipe possa usar desde o primeiro dia.'
        },
        {
          icon: 'workflow',
          title: 'Governanca e evolucao',
          description:
            'Instalamos ownership, controle de qualidade e melhoria continua para que o CRM cresca junto com a empresa.'
        }
      ]
    },
    capabilities: {
      title: 'O que uma operacao madura precisa',
      subtitle:
        'Um CRM bem assessorado deve organizar a execucao diaria e ao mesmo tempo dar visibilidade real para a lideranca.',
      list: [
        'Pipeline com criterios claros de avancar e sair',
        'Automacoes para acompanhamento, atribuicao e alertas',
        'Governanca de campos, responsaveis e SLA',
        'Visibilidade do ciclo comercial e de atendimento',
        'Paineis acionaveis para direcao e gestao',
        'Integracoes com canais, formularios e sistemas chave'
      ]
    },
    workspace: {
      title: 'As camadas que alinhamos',
      subtitle:
        'A assessoria CRM cruza operacao, equipe, dados e controle para que todos trabalhem com o mesmo mapa.',
      areas: ['Pipeline', 'Acompanhamento', 'Atendimento', 'Relatorios', 'Governanca'],
      differentiators: [
        'Menos criterio individual e mais regras compartilhadas',
        'Tempos de resposta mensuraveis e auditaveis',
        'Dados mais limpos para decisoes comerciais',
        'Menos atrito entre vendas, operacoes e suporte'
      ]
    },
    journey: {
      title: 'Roadmap de assessoria CRM',
      subtitle:
        'Vamos do diagnostico ate a adocao com uma sequencia pensada para fazer o sistema refletir a realidade da equipe.',
      flowTitle: 'Do caos comercial a uma operacao rastreavel',
      rolesTitle: 'Criterio de ownership',
      rolesDesc:
        'Definimos quem captura, quem qualifica, quem executa, quem supervisiona e quem responde pela qualidade do dado em cada etapa.',
      steps: [
        { title: 'Diagnostico', description: 'Mapeamos o fluxo atual, gargalos e perda de contexto.' },
        { title: 'Arquitetura', description: 'Desenhamos pipeline, estados, responsaveis e regras de negocio.' },
        { title: 'Implementacao', description: 'Configuramos automacoes, formularios, visoes e paineis.' },
        { title: 'Adocao', description: 'Levamos o uso por funcao com criterios simples e mensuraveis.' },
        { title: 'Controle', description: 'Instalamos indicadores para lideranca, gestores e acompanhamento diario.' },
        { title: 'Melhoria continua', description: 'Ajustamos de acordo com friccoes reais e novas prioridades do negocio.' }
      ]
    },
    security: {
      title: 'Governanca do dado comercial',
      subtitle:
        'Seguranca em CRM nao e apenas acesso. Tambem e contexto, qualidade, consistencia e rastreabilidade.',
      layers: [
        {
          icon: 'shield',
          title: 'Acesso por papel',
          description: 'Definimos permissoes, responsaveis e escopo operacional para reduzir erros e exposicoes desnecessarias.'
        },
        {
          icon: 'layers',
          title: 'Estrutura consistente',
          description: 'Padronizamos campos, catalogos e estados para que a informacao seja comparavel e util.'
        },
        {
          icon: 'send',
          title: 'Rastreabilidade',
          description: 'Cada mudanca relevante deve deixar rastro para supervisionar cumprimento, acompanhamento e qualidade.'
        },
        {
          icon: 'file',
          title: 'Documentacao operacional',
          description: 'O sistema precisa de criterio escrito para que a equipe nao dependa de memoria informal.'
        }
      ]
    },
    implementation: {
      title: 'O que resolvemos em uma assessoria CRM',
      subtitle:
        'A maioria dos problemas nao aparece por falta de tecnologia, e sim por falta de desenho operacional e governanca.',
      problems: [
        { title: 'Pipeline inflado', description: 'Etapas ambiguas, oportunidades mal qualificadas e acompanhamento sem criterio compartilhado.' },
        { title: 'Automacao desorganizada', description: 'Regras sobrepostas, notificacoes irrelevantes e tarefas sem dono claro.' },
        { title: 'Dado fraco', description: 'Campos incompletos, capturas inconsistentes e relatorios em que ninguem confia.' },
        { title: 'Baixa adocao', description: 'A equipe sente que o CRM gera atrito em vez de acelerar a operacao.' }
      ],
      deliverablesTitle: 'Entregaveis de uma operacao CRM bem assessorada',
      deliverablesSubtitle:
        'Nao deixamos apenas configuracao. Deixamos estrutura, criterio e visibilidade para sustentar o uso diario.',
      deliverables: [
        { icon: 'workflow', title: 'Pipeline e regras', description: 'Etapas, ownership, handoffs e criterios de avancar documentados.' },
        { icon: 'file', title: 'Modelo de dados', description: 'Campos-chave, taxonomias e regras de captura alinhadas ao negocio.' },
        { icon: 'bot', title: 'Automacao util', description: 'Acompanhamentos, alertas e tarefas que reduzem atrito e tempo ocioso.' },
        { icon: 'shield', title: 'Governanca', description: 'Permissoes, responsabilidades e criterios de qualidade para manter ordem.' },
        { icon: 'cpu', title: 'Integracoes chave', description: 'Conectamos formularios, canais e sistemas onde realmente geram valor.' },
        { icon: 'gauge', title: 'KPIs acionaveis', description: 'Paineis para lideranca e gestao com foco em conversao, tempo e controle.' }
      ],
      dashboardTitle: 'Sinais que valem acompanhar',
      gauges: [
        { label: 'Visibilidade', value: 96, caption: 'A lideranca entende o que esta acontecendo e onde precisa intervir.' },
        { label: 'Automacao util', value: 82, caption: 'As regras aliviam a operacao em vez de adicionar ruido.' },
        { label: 'Qualidade do dado', value: 89, caption: 'A equipe captura informacao que realmente serve para decidir.' }
      ],
      layers: {
        title: 'CRM em camadas',
        core: 'CRM',
        coreDesc: 'Execucao diaria',
        outer: 'Processo',
        outerDesc: 'Regras e ownership',
        security: 'Governanca',
        securityDesc: 'Qualidade, rastreabilidade e controle',
        description:
          'O CRM e apenas o nucleo. Em volta dele precisam existir criterio de processo, governanca do dado e acompanhamento continuo para que a operacao escale com ordem.'
      },
      pillars: [
        {
          icon: 'file',
          title: 'DAP',
          description: 'Mapeamos o processo, documentamos decisoes e aterrissamos o criterio operacional.',
          href: '/dap',
          ctaLabel: 'Explorar'
        },
        {
          icon: 'workflow',
          title: 'SDI',
          description: 'Configuramos e implementamos a arquitetura necessaria para operar o CRM com ordem.',
          href: '/sdi',
          ctaLabel: 'Explorar'
        },
        {
          icon: 'bar',
          title: 'SMC',
          description: 'Auditamos, melhoramos e evoluimos a operacao conforme a realidade do negocio.',
          href: '/smc',
          ctaLabel: 'Explorar'
        },
        {
          icon: 'phone',
          title: 'Suporte Pro',
          description: 'Sustentamos a operacao com acompanhamento tecnico e resolucao orientada a continuidade.',
          href: '/soporte',
          ctaLabel: 'Ver Suporte Pro'
        }
      ]
    },
    packs: {
      title: 'Tres niveis de acompanhamento CRM',
      subtitle:
        'Voce pode comecar pelo diagnostico, avancar para implementacao guiada ou trabalhar uma camada integral de arquitetura e melhoria continua.',
      viewMore: 'Ver detalhe',
      viewLess: 'Ver menos',
      evaluate: 'Quero avaliar minha operacao',
      items: [
        {
          title: 'Foundation',
          subtitle: 'Organizar a base operacional',
          recommended: false,
          links: ['DAP', 'SDI', 'Suporte Pro'],
          sections: [
            { label: 'Diagnostico', value: 'Base clara', width: '42%', tone: 'gray' },
            { label: 'Pipeline', value: 'Estrutura inicial', width: '48%', tone: 'gray' },
            { label: 'Acompanhamento', value: 'Regras essenciais', width: '54%', tone: 'emerald' },
            { label: 'Paineis', value: 'Visibilidade inicial', width: '45%', tone: 'gray' },
            { label: 'Governanca', value: 'Criterio minimo viavel', width: '40%', tone: 'gray' },
            { label: 'Adocao', value: 'Inicio controlado', width: '44%', tone: 'gray' }
          ]
        },
        {
          title: 'Growth',
          subtitle: 'Escalar com mais controle',
          recommended: true,
          links: ['DAP', 'SDI', 'SMC', 'API Tool', 'Suporte Pro'],
          sections: [
            { label: 'Diagnostico', value: 'Cobertura por area', width: '66%', tone: 'blue' },
            { label: 'Pipeline', value: 'Fluxos por etapa', width: '72%', tone: 'blue' },
            { label: 'Acompanhamento', value: 'Automacao util', width: '76%', tone: 'emerald' },
            { label: 'Paineis', value: 'KPIs por papel', width: '70%', tone: 'blue' },
            { label: 'Governanca', value: 'Regras auditaveis', width: '68%', tone: 'blue' },
            { label: 'Adocao', value: 'Uso por ownership', width: '63%', tone: 'blue' }
          ]
        },
        {
          title: 'Scale',
          subtitle: 'Arquitetura e melhoria continua',
          recommended: false,
          links: ['DAP', 'SDI', 'SMC', 'API Tool', 'Microservicio', 'Suporte Pro'],
          sections: [
            { label: 'Diagnostico', value: 'Cobertura executiva', width: '90%', tone: 'indigo' },
            { label: 'Pipeline', value: 'Desenho multiprocesso', width: '88%', tone: 'indigo' },
            { label: 'Acompanhamento', value: 'Regras avancadas', width: '86%', tone: 'emerald' },
            { label: 'Paineis', value: 'Leitura diretiva', width: '92%', tone: 'indigo' },
            { label: 'Governanca', value: 'Controle integral', width: '94%', tone: 'indigo' },
            { label: 'Adocao', value: 'Evolucao sustentada', width: '82%', tone: 'indigo' }
          ]
        }
      ]
    },
    plans: {
      title: 'Onde um advisor CRM gera valor',
      labels: {
        focus: 'Foco',
        withNextcoders: 'Com Nextcoders',
        fit: 'Ideal para'
      },
      items: [
        {
          title: 'Operacao comercial',
          desc: 'Quando a equipe precisa de ordem, acompanhamento e visibilidade em vez de depender de criterio informal.',
          focus: 'Processos, pipeline e ownership',
          withNextcoders: ['Regras', 'Paineis', 'Acompanhamento'],
          fit: ['Vendas', 'Account management', 'Atendimento comercial'],
          tone: 'gray'
        },
        {
          title: 'Operacao de atendimento',
          desc: 'Quando a continuidade do cliente exige handoffs claros, rastreabilidade e tempos de resposta mensuraveis.',
          focus: 'Casos, SLA e continuidade',
          withNextcoders: ['Alertas', 'SLA', 'Supervisao'],
          fit: ['Customer success', 'Suporte', 'Pos-venda'],
          tone: 'blue'
        },
        {
          title: 'Operacao integrada',
          desc: 'Quando CRM, formularios, canais e sistemas precisam trabalhar sob uma unica logica operacional.',
          focus: 'Integracao e governanca',
          withNextcoders: ['Integracoes', 'API Tool', 'Microservicio'],
          fit: ['Direcao', 'Operacoes', 'Equipes multi-area'],
          tone: 'indigo'
        }
      ]
    },
    benefits: {
      title: 'Beneficios de uma boa assessoria CRM',
      subtitle:
        'Um CRM forte faz mais do que capturar oportunidades. Ele organiza a execucao, acelera a resposta e melhora a leitura do negocio.',
      items: [
        {
          icon: 'bar',
          title: 'Visibilidade executiva',
          description: 'A lideranca decide com dados operacionais consistentes e nao com percepcoes fragmentadas.'
        },
        {
          icon: 'message',
          title: 'Menos atrito entre equipes',
          description: 'Vendas, atendimento e operacoes compartilham a mesma verdade e melhores handoffs.'
        },
        {
          icon: 'shield',
          title: 'Evolucao sustentavel',
          description: 'A operacao pode crescer sem perder rastreabilidade, ordem e criterio de qualidade.'
        }
      ]
    },
    final: {
      title: 'Pronto para organizar sua operacao CRM com criterio senior?',
      desc:
        'Nao vendemos apenas uma ferramenta. Ajudamos voce a construir uma operacao comercial que a equipe consiga executar e a lideranca consiga governar.',
      primary: 'Falar com um consultor senior',
      secondary: 'Ver Suporte Pro',
      note: 'Nossos tempos de resposta seguem os padroes do Suporte Pro.',
      explore: 'Voce tambem pode explorar:',
      and: 'e'
    }
  }
};

const ICONS = {
  layers: Layers,
  rocket: Rocket,
  workflow: Workflow,
  shield: ShieldCheck,
  send: Send,
  file: FileText,
  bot: Bot,
  cpu: Cpu,
  gauge: Gauge,
  bar: BarChart3,
  message: MessageCircle,
  phone: Phone
};

const toneClasses = {
  gray: {
    badge: 'bg-white border border-gray-200 text-gray-700',
    bar: 'bg-gray-700',
    card: 'border-gray-200',
    icon: 'text-gray-700'
  },
  blue: {
    badge: 'bg-white border border-blue-200 text-blue-700',
    bar: 'bg-blue-600',
    card: 'border-blue-200',
    icon: 'text-blue-700'
  },
  indigo: {
    badge: 'bg-white border border-indigo-200 text-indigo-800',
    bar: 'bg-indigo-700',
    card: 'border-indigo-200',
    icon: 'text-indigo-800'
  },
  emerald: {
    badge: 'bg-white border border-emerald-200 text-emerald-700',
    bar: 'bg-emerald-600',
    card: 'border-emerald-200',
    icon: 'text-emerald-700'
  }
};

const PageHeader = ({ copy, onOpenPopup }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-6">
      <Link to="/" className="flex items-center flex-shrink-0">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders'; }}
        />
      </Link>
      <nav className="flex-1 overflow-x-auto">
        <div className="flex items-center justify-end whitespace-nowrap text-sm font-semibold text-gray-700 divide-x divide-gray-200/40">
          <Link to="/crm" className="px-4 hover:text-[#141414] transition">CRM</Link>
          <Link to="/dap" className="px-4 hover:text-[#141414] transition">DAP</Link>
          <Link to="/sdi" className="px-4 hover:text-[#141414] transition">SDI</Link>
          <Link to="/smc" className="px-4 hover:text-[#141414] transition">SMC</Link>
          <Link to="/soporte" className="px-4 hover:text-[#141414] transition">{copy.nav.support}</Link>
        </div>
      </nav>
      <div className="flex items-center gap-4 md:gap-8">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="flex-shrink-0 bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {copy.nav.cta}
        </button>
      </div>
    </div>
  </header>
);

const InfoCard = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-7 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className={`p-3 rounded-xl w-fit mb-4 ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const PillarCard = ({ href, icon: Icon, title, description, color, ctaLabel }) => (
  <a
    href={href}
    className="group bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow duration-300 h-full block"
  >
    <div className={`p-3 rounded-xl w-fit mb-4 ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:underline">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    <div className="mt-5 text-sm font-bold text-[#141414]">{ctaLabel}</div>
  </a>
);

const ProblemTile = ({ title, description }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6">
    <div className="text-base font-bold text-gray-900">{title}</div>
    <div className="text-sm text-gray-600 mt-2 leading-relaxed">{description}</div>
  </div>
);

const BenefitCard = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className={`p-3 rounded-xl w-fit mb-4 ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const StepPill = ({ index, title, description, accentClass }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6">
    <div className="flex items-start gap-4">
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-extrabold text-sm ${accentClass}`}>
        {index}
      </div>
      <div>
        <div className="text-lg font-bold text-[#141414]">{title}</div>
        <div className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</div>
      </div>
    </div>
  </div>
);

const DialGauge = ({ label, value, caption, colorClass = 'text-indigo-700', trackClass = 'text-gray-200' }) => {
  const size = 160;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const startAngle = -210;
  const endAngle = 30;
  const angle = startAngle + ((endAngle - startAngle) * Math.max(0, Math.min(100, value))) / 100;
  const toRadians = (deg) => (deg * Math.PI) / 180;

  const polarToCartesian = (cx, cy, r, deg) => {
    const rad = toRadians(deg);
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const start = polarToCartesian(center, center, radius, startAngle);
  const end = polarToCartesian(center, center, radius, endAngle);
  const needleEnd = polarToCartesian(center, center, radius - 8, angle);
  const trackPath = `M ${start.x} ${start.y} A ${radius} ${radius} 0 1 1 ${end.x} ${end.y}`;

  return (
    <div className="bg-white border border-gray-200 rounded-3xl p-6">
      <div className="text-sm font-bold text-gray-900 mb-4">{label}</div>
      <div className="flex items-center justify-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <path d={trackPath} fill="none" stroke="currentColor" className={trackClass} strokeWidth={strokeWidth} strokeLinecap="round" />
          <path
            d={trackPath}
            fill="none"
            stroke="currentColor"
            className={colorClass}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${(Math.max(0, Math.min(100, value)) / 100) * 100} 100`}
            pathLength="100"
          />
          <line x1={center} y1={center} x2={needleEnd.x} y2={needleEnd.y} stroke="currentColor" className="text-gray-900" strokeWidth="3" strokeLinecap="round" />
          <circle cx={center} cy={center} r="6" fill="currentColor" className="text-gray-900" />
          <text x={center} y={center + 34} textAnchor="middle" className="fill-gray-900" fontSize="22" fontWeight="700">
            {Math.round(value)}%
          </text>
        </svg>
      </div>
      <div className="mt-2 text-sm text-gray-600 leading-relaxed">{caption}</div>
    </div>
  );
};

const ConcentricLayers = ({ copy }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-8 h-full">
    <div className="flex items-center gap-3 mb-4">
      <Layers className="text-indigo-700" size={26} />
      <h3 className="text-xl font-bold text-gray-900">{copy.title}</h3>
    </div>
    <div className="relative mx-auto w-full max-w-sm aspect-square">
      <div className="absolute inset-0 rounded-full bg-indigo-50 border border-indigo-100" />
      <div className="absolute inset-[10%] rounded-full bg-blue-50 border border-blue-100" />
      <div className="absolute inset-[22%] rounded-full bg-white border border-gray-200" />
      <div className="absolute inset-[30%] rounded-full bg-[#141414] flex items-center justify-center text-center px-6">
        <div>
          <div className="text-xs font-semibold text-gray-300">{copy.coreDesc}</div>
          <div className="text-lg font-extrabold text-white">{copy.core}</div>
          <div className="text-xs text-gray-300 mt-1">{copy.coreDesc}</div>
        </div>
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 top-[8%] text-center px-3 py-2 rounded-2xl bg-white/80 backdrop-blur border border-indigo-100">
        <div className="text-xs font-semibold text-indigo-700">{copy.outer}</div>
        <div className="text-sm font-bold text-gray-900">{copy.outerDesc}</div>
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[10%] text-center px-3 py-2 rounded-2xl bg-white/80 backdrop-blur border border-blue-100">
        <div className="text-xs font-semibold text-blue-700">{copy.security}</div>
        <div className="text-sm font-bold text-gray-900">{copy.securityDesc}</div>
      </div>
    </div>
    <p className="mt-6 text-sm text-gray-600 leading-relaxed">{copy.description}</p>
  </div>
);

const PackCard = ({ item, viewMoreLabel, viewLessLabel, evaluateLabel }) => {
  const [showAll, setShowAll] = useState(false);
  const visibleSections = showAll ? item.sections : item.sections.slice(0, 4);
  const hiddenCount = Math.max(0, item.sections.length - visibleSections.length);

  return (
    <div className={`relative bg-white border rounded-3xl p-7 flex flex-col h-full ${item.recommended ? 'border-blue-500 shadow-xl' : 'border-gray-200'}`}>
      <div className="text-center">
        <div className="text-2xl font-extrabold text-[#141414]">{item.title}</div>
        <div className="text-sm text-gray-500 mt-1">{item.subtitle}</div>
      </div>
      <div className="mt-6 flex-grow">
        <div className="space-y-4">
          {visibleSections.map((section) => {
            const tone = toneClasses[section.tone] || toneClasses.gray;
            return (
              <div key={section.label} className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm font-bold text-gray-900">{section.label}</div>
                  <div className={`text-xs font-semibold px-2.5 py-1 rounded-full ${tone.badge}`}>{section.value}</div>
                </div>
                <div className="mt-3 h-2 bg-white rounded-full overflow-hidden border border-gray-100">
                  <div className={`h-full ${tone.bar}`} style={{ width: section.width }} />
                </div>
              </div>
            );
          })}
        </div>
        {item.sections.length > 4 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="text-sm font-bold text-[#141414] hover:text-gray-700 transition"
            >
              {showAll ? viewLessLabel : `${viewMoreLabel} (+${hiddenCount})`}
            </button>
          </div>
        )}
      </div>
      {!!item.links?.length && (
        <div className="mt-6 flex flex-wrap gap-2">
          {item.links.map((label) => {
            const hrefMap = {
              DAP: '/dap',
              SDI: '/sdi',
              SMC: '/smc',
              'API Tool': '/api-tool',
              Microservicio: '/microservicio',
              'Pro Support': '/soporte',
              'Soporte Pro': '/soporte',
              'Suporte Pro': '/soporte'
            };
            return (
              <a
                key={label}
                href={hrefMap[label] || '#cta'}
                className="px-3 py-1.5 rounded-full text-xs font-bold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition"
              >
                {label}
              </a>
            );
          })}
        </div>
      )}
      <button
        onClick={() => { window.location.hash = '#cta'; }}
        className={`mt-6 w-full py-3 rounded-2xl font-bold transition ${item.recommended ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-[#141414] text-white hover:bg-gray-900'}`}
      >
        {evaluateLabel}
      </button>
    </div>
  );
};

const CRMPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const heroImageUrl = '/images/bitrix24/bitrix24.png';
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').split('-')[0];
  const copy = COPY[lang] || COPY.es;

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const overviewItems = useMemo(() => (
    copy.overview.items.map((item) => ({
      ...item,
      icon: ICONS[item.icon],
      color:
        item.icon === 'layers'
          ? { bg: 'bg-indigo-50', text: 'text-indigo-700' }
          : item.icon === 'rocket'
            ? { bg: 'bg-blue-50', text: 'text-blue-700' }
            : { bg: 'bg-emerald-50', text: 'text-emerald-700' }
    }))
  ), [copy.overview.items]);

  const securityLayers = useMemo(() => (
    copy.security.layers.map((item, index) => ({
      ...item,
      icon: ICONS[item.icon],
      color: [
        { bg: 'bg-emerald-50', text: 'text-emerald-700' },
        { bg: 'bg-indigo-50', text: 'text-indigo-700' },
        { bg: 'bg-blue-50', text: 'text-blue-700' },
        { bg: 'bg-gray-50', text: 'text-gray-700' }
      ][index]
    }))
  ), [copy.security.layers]);

  const deliverables = useMemo(() => (
    copy.implementation.deliverables.map((item, index) => ({
      ...item,
      icon: ICONS[item.icon],
      color: [
        { bg: 'bg-indigo-50', text: 'text-indigo-700' },
        { bg: 'bg-gray-50', text: 'text-gray-700' },
        { bg: 'bg-emerald-50', text: 'text-emerald-700' },
        { bg: 'bg-blue-50', text: 'text-blue-700' },
        { bg: 'bg-purple-50', text: 'text-purple-700' },
        { bg: 'bg-orange-50', text: 'text-orange-700' }
      ][index]
    }))
  ), [copy.implementation.deliverables]);

  const benefits = useMemo(() => (
    copy.benefits.items.map((item, index) => ({
      ...item,
      icon: ICONS[item.icon],
      color: [
        { bg: 'bg-blue-50', text: 'text-blue-700' },
        { bg: 'bg-emerald-50', text: 'text-emerald-700' },
        { bg: 'bg-indigo-50', text: 'text-indigo-700' }
      ][index]
    }))
  ), [copy.benefits.items]);

  const pillars = useMemo(() => (
    copy.implementation.pillars.map((item, index) => ({
      ...item,
      icon: ICONS[item.icon],
      color: [
        { bg: 'bg-blue-50', text: 'text-blue-700' },
        { bg: 'bg-indigo-50', text: 'text-indigo-700' },
        { bg: 'bg-emerald-50', text: 'text-emerald-700' },
        { bg: 'bg-purple-50', text: 'text-purple-700' }
      ][index]
    }))
  ), [copy.implementation.pillars]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button onClick={closePopup} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition" aria-label={t('common.back')}>
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('common.diagnostic_request')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm formData="inline/4/10oz6w" formId="4" scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js" />
            </div>
          </div>
        </div>
      )}

      <PageHeader copy={copy} onOpenPopup={openPopup} />

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-screen">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {t('common.back')}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt="CRM advisory by Nextcoders"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=CRM'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 p-8 md:p-14 min-h-screen flex flex-col justify-end max-w-7xl mx-auto">
              <div className="inline-flex items-center gap-3 bg-white/10 text-white px-4 py-2 rounded-full w-fit mb-6 backdrop-blur border border-white/15">
                <Layers size={18} />
                <span className="text-sm font-semibold">{copy.hero.badge}</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight max-w-5xl">
                {copy.hero.title}
              </h1>

              <p className="mt-6 text-xl md:text-2xl text-white/90 max-w-4xl font-medium">
                {copy.hero.subtitle}
              </p>

              <p className="mt-4 text-white/75 max-w-4xl leading-relaxed">
                {copy.hero.desc}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button onClick={openPopup} className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition">
                  {copy.hero.ctaPrimary}
                </button>
                <a href="#packs" className="bg-transparent border border-white/40 text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition">
                  {copy.hero.ctaSecondary}
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{copy.overview.title}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">{copy.overview.subtitle}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
                {overviewItems.map((item) => (
                  <InfoCard key={item.title} icon={item.icon} title={item.title} description={item.description} color={item.color} />
                ))}
              </div>
            </section>

            <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                <div className="bg-white border border-gray-200 rounded-3xl p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <BarChart3 className="text-indigo-700" size={26} />
                    <h3 className="text-xl font-bold text-gray-900">{copy.capabilities.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">{copy.capabilities.subtitle}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {copy.capabilities.list.map((capability) => (
                      <div key={capability} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-2xl p-4">
                        <CheckCircle size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-700 leading-relaxed">{capability}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-3xl p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <Users className="text-blue-700" size={26} />
                    <h3 className="text-xl font-bold text-gray-900">{copy.workspace.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">{copy.workspace.subtitle}</p>
                  <div className="flex flex-wrap gap-3">
                    {copy.workspace.areas.map((area, index) => {
                      const classes = [
                        'bg-indigo-50 text-indigo-700 border-indigo-100',
                        'bg-blue-50 text-blue-700 border-blue-100',
                        'bg-emerald-50 text-emerald-700 border-emerald-100',
                        'bg-purple-50 text-purple-700 border-purple-100',
                        'bg-gray-50 text-gray-700 border-gray-100'
                      ];
                      return (
                        <div key={area} className={`px-4 py-2 rounded-full border text-sm font-bold ${classes[index]}`}>
                          {area}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {copy.workspace.differentiators.map((item) => (
                      <div key={item} className="flex items-start gap-3 bg-white border border-gray-100 rounded-2xl p-4">
                        <CheckCircle size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-700 leading-relaxed">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{copy.journey.title}</h2>
                <p className="text-gray-600 max-w-4xl mx-auto">{copy.journey.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                <div className="bg-white border border-gray-200 rounded-3xl p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <Workflow className="text-emerald-700" size={26} />
                    <h3 className="text-xl font-bold text-gray-900">{copy.journey.flowTitle}</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {copy.journey.steps.map((step, idx) => (
                      <StepPill
                        key={step.title}
                        index={idx + 1}
                        title={step.title}
                        description={step.description}
                        accentClass={['bg-indigo-50 text-indigo-700', 'bg-blue-50 text-blue-700', 'bg-emerald-50 text-emerald-700', 'bg-purple-50 text-purple-700', 'bg-gray-50 text-gray-700', 'bg-orange-50 text-orange-700'][idx]}
                      />
                    ))}
                    <div className="sm:col-span-2 bg-gray-50 border border-gray-200 rounded-3xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-2xl bg-white border border-gray-200 text-gray-800">
                          <Users size={22} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-lg font-bold text-[#141414]">{copy.journey.rolesTitle}</div>
                          <div className="text-sm text-gray-600 mt-1 leading-relaxed">{copy.journey.rolesDesc}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-3xl p-8 h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <ShieldCheck className="text-indigo-700" size={26} />
                    <h3 className="text-xl font-bold text-gray-900">{copy.security.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">{copy.security.subtitle}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {securityLayers.map((layer) => (
                      <InfoCard key={layer.title} icon={layer.icon} title={layer.title} description={layer.description} color={layer.color} />
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{copy.implementation.title}</h2>
              <p className="text-center text-gray-600 max-w-4xl mx-auto mb-12">{copy.implementation.subtitle}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {copy.implementation.problems.map((item) => (
                  <ProblemTile key={item.title} title={item.title} description={item.description} />
                ))}
              </div>

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-[#141414] mb-3">{copy.implementation.deliverablesTitle}</h3>
                <p className="text-gray-600 max-w-4xl mx-auto">{copy.implementation.deliverablesSubtitle}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {deliverables.map((item) => (
                  <InfoCard key={item.title} icon={item.icon} title={item.title} description={item.description} color={item.color} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                <div className="bg-white border border-gray-200 rounded-3xl p-8 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <Gauge className="text-indigo-700" size={26} />
                    <h3 className="text-xl font-bold text-gray-900">{copy.implementation.dashboardTitle}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {copy.implementation.gauges.map((gauge, index) => (
                      <DialGauge
                        key={gauge.label}
                        label={gauge.label}
                        value={gauge.value}
                        caption={gauge.caption}
                        colorClass={['text-emerald-600', 'text-indigo-600', 'text-blue-600'][index]}
                        trackClass={['text-emerald-100', 'text-indigo-100', 'text-blue-100'][index]}
                      />
                    ))}
                  </div>
                </div>

                <ConcentricLayers copy={copy.implementation.layers} />
              </div>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {pillars.map((item) => (
                  <PillarCard key={item.title} href={item.href} icon={item.icon} title={item.title} description={item.description} color={item.color} ctaLabel={item.ctaLabel} />
                ))}
              </div>
            </section>

            <section id="packs" className="mb-24">
              <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{copy.packs.title}</h2>
              <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">{copy.packs.subtitle}</p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                {copy.packs.items.map((item) => (
                  <PackCard
                    key={item.title}
                    item={item}
                    viewMoreLabel={copy.packs.viewMore}
                    viewLessLabel={copy.packs.viewLess}
                    evaluateLabel={copy.packs.evaluate}
                  />
                ))}
              </div>
            </section>

            <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-center text-[#141414] mb-10">{copy.plans.title}</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {copy.plans.items.map((plan, index) => {
                  const tone = toneClasses[plan.tone] || toneClasses.gray;
                  const cardBorder = index === 1 ? 'border-blue-200 shadow-lg' : tone.card;
                  const cardIcon = index === 0 ? Rocket : index === 1 ? Workflow : Cpu;
                  const Icon = cardIcon;
                  return (
                    <div key={plan.title} className={`bg-white border rounded-3xl p-8 ${cardBorder}`}>
                      <div className="flex items-center gap-3 mb-5">
                        <Icon className={tone.icon} size={24} />
                        <h3 className="text-xl font-bold text-gray-900">{plan.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-6">{plan.desc}</p>
                      <div className="grid grid-cols-1 gap-3">
                        <div className={`rounded-2xl p-4 flex items-start gap-3 ${index === 1 ? 'bg-blue-50 border border-blue-100' : index === 2 ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-200'}`}>
                          <Layers size={18} className={`${tone.icon} mt-0.5 flex-shrink-0`} />
                          <div>
                            <div className="text-xs font-extrabold text-gray-900">{copy.plans.labels.focus}</div>
                            <div className="text-sm text-gray-700 leading-relaxed">{plan.focus}</div>
                          </div>
                        </div>
                        <div className={`rounded-2xl p-4 flex items-start gap-3 ${index === 1 ? 'bg-blue-50 border border-blue-100' : index === 2 ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-200'}`}>
                          <CheckCircle size={18} className={`${tone.icon} mt-0.5 flex-shrink-0`} />
                          <div>
                            <div className="text-xs font-extrabold text-gray-900">{copy.plans.labels.withNextcoders}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {plan.withNextcoders.map((tag) => (
                                <span key={tag} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${tone.badge}`}>{tag}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-start gap-3">
                          <Users size={18} className="text-gray-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-xs font-extrabold text-gray-900">{copy.plans.labels.fit}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {plan.fit.map((tag) => (
                                <span key={tag} className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-gray-800">{tag}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex flex-wrap gap-2">
                        <a href="/dap" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition">DAP</a>
                        <a href="/sdi" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition">SDI</a>
                        <a href="/smc" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition">SMC</a>
                        {index > 0 && <a href="/api-tool" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition">API Tool</a>}
                        {index === 2 && <a href="/microservicio" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition">Microservicio</a>}
                        <a href="/soporte" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-white text-[#141414] hover:bg-gray-50 transition">{copy.nav.support}</a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{copy.benefits.title}</h2>
                <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">{copy.benefits.subtitle}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {benefits.map((item) => (
                  <BenefitCard key={item.title} icon={item.icon} title={item.title} description={item.description} color={item.color} />
                ))}
              </div>
            </section>

            <section id="cta" className="text-center bg-[#141414] rounded-3xl p-12 text-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-indigo-900/20 to-blue-900/20 z-0" />
              <div className="relative z-10">
                <ShieldCheck size={48} className="mx-auto mb-6 text-indigo-300" />
                <h2 className="text-3xl font-bold mb-6">{copy.final.title}</h2>
                <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">{copy.final.desc}</p>
                <div className="flex flex-col md:flex-row justify-center gap-4">
                  <button onClick={openPopup} className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition">
                    {copy.final.primary}
                  </button>
                  <a href="/soporte" className="bg-transparent border border-white text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition flex items-center justify-center gap-2">
                    <Phone size={18} />
                    {copy.final.secondary}
                  </a>
                </div>
                <div className="mt-8 text-sm text-gray-400 flex items-center justify-center gap-2">
                  <Clock size={16} />
                  <span>{copy.final.note}</span>
                </div>
                <div className="mt-6 text-xs text-gray-500 flex items-center justify-center gap-2">
                  <Send size={16} />
                  <span>{copy.final.explore} <a className="underline hover:text-white" href="/api-tool">API Tool</a> {copy.final.and} <a className="underline hover:text-white" href="/microservicio">Microservicio</a>.</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CRMPage;

